import { onMounted, onUnmounted } from 'vue'
import {
  getEnterpriseConfigByDomain,
  getEnterpriseFileByDomain
} from '@/api/personalization'
import { useSystemBrandStore } from '@/stores/system-brand'
import {
  applyFavicon,
  applyPublicTenantFavicon,
  releasePublicTenantFavicon
} from '@/composables/useSystemBrandApply'

const FAVICON_CACHE_PREFIX = 'ct_public_tenant_favicon:'

/**
 * 未登录登录页：按当前域名读取租户配置的系统图标并应用。
 * 租户端登录页、客户端登录页共用，避免只在其中一个页面生效。
 *
 * 只有真正取到租户图标后才加锁；没有返回时释放锁，让全局系统品牌继续兜底。
 */
export function usePublicTenantFavicon() {
  const cacheKey = `${FAVICON_CACHE_PREFIX}${window.location.hostname}`
  const systemBrandStore = useSystemBrandStore()
  let disposed = false

  releasePublicTenantFavicon()

  // 先用上次成功获取的图标，减少首次渲染时先显示默认图标的时间
  try {
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
      applyFavicon(cached)
    }
  } catch {
    // localStorage 不可用时忽略缓存
  }

  const fallbackToSystemBrand = () => {
    if (disposed) return
    releasePublicTenantFavicon()
    systemBrandStore.applySystemBrand()
  }

  const applyTenantFavicon = async (fileId: string) => {
    try {
      const dataUrl = await getEnterpriseFileByDomain(window.location.hostname, fileId)
      if (disposed) return
      if (dataUrl) {
        applyPublicTenantFavicon(dataUrl)
        try {
          localStorage.setItem(cacheKey, dataUrl)
        } catch {
          // localStorage 写入失败不影响图标应用
        }
      } else {
        fallbackToSystemBrand()
      }
    } catch (error) {
      if (disposed) return
      console.warn('[CT_PUBLIC_BRAND] 获取企业系统图标失败，使用全局系统品牌兜底', error)
      fallbackToSystemBrand()
    }
  }

  onMounted(async () => {
    try {
      const config = await getEnterpriseConfigByDomain(window.location.hostname)
      if (disposed) return
      if (config?.systemIconFile) {
        void applyTenantFavicon(config.systemIconFile)
      } else {
        // 租户未配置系统图标时，清掉旧缓存并交回全局系统品牌兜底
        try {
          localStorage.removeItem(cacheKey)
        } catch {
          // ignore
        }
        fallbackToSystemBrand()
      }
    } catch (error) {
      if (disposed) return
      console.warn('[CT_PUBLIC_BRAND] 获取企业配置失败，使用全局系统品牌兜底', error)
      fallbackToSystemBrand()
    }
  })

  onUnmounted(() => {
    // 离开登录页后解除锁定，进入主布局后由系统品牌重新接管
    disposed = true
    releasePublicTenantFavicon()
  })
}
